import { addDays as addDaysFns, format } from 'date-fns';

/**
 * Parse an ISO date (YYYY-MM-DD) as a *local* date pinned to 12:00.
 *
 * The noon anchor is deliberate and load-bearing: `parseISO('2026-08-24')`
 * yields UTC midnight, which in any timezone west of UTC lands on the previous
 * calendar day, shifting every holiday and every week boundary by one day. The
 * original app solved this the same way (`dISO`, legacy/index.html:214) and the
 * behaviour is preserved exactly.
 */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12, 0, 0, 0);
}

/** Format a Date back to a local ISO date (YYYY-MM-DD), never UTC-shifted. */
export function toLocalISO(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Add days, preserving the noon anchor. */
export function addDays(date: Date, amount: number): Date {
  return addDaysFns(date, amount);
}

/** "24 août" — the day label shown in week-grid headers. */
export function frDayDate(date: Date): string {
  return date.toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' });
}

/**
 * "Semaine du 24 au 28 août 2026", or the cross-month form
 * "Semaine du 29 juin au 3 juillet 2027".
 */
export function frWeekRange(monday: Date): string {
  const friday = addDays(monday, 4);
  const sameMonth = monday.getMonth() === friday.getMonth();

  if (sameMonth) {
    const tail = friday.toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
    return `Semaine du ${monday.getDate()} au ${friday.getDate()} ${tail}`;
  }

  const tail = friday.toLocaleDateString('fr-BE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `Semaine du ${frDayDate(monday)} au ${tail}`;
}

/** Today's date in the long form used on the home page hero. */
export function frLongToday(now: Date = new Date()): string {
  return now.toLocaleDateString('fr-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Today as a local ISO date, for prefilling `<input type="date">`. */
export function todayISO(now: Date = new Date()): string {
  return toLocalISO(now);
}
